import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@trillion/ui/components/ui/button";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { BrandForm } from "@/components/brands/brand-form";
import { adminApi } from "@/lib/admin-api";
import { describeApiError } from "@/lib/api-error";
import type { ProductBrand } from "@/types";

export function BrandEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const brandId = Number(id);

  const [brand, setBrand] = useState<ProductBrand | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchBrand() {
      try {
        const data = await adminApi.brands.get(brandId);
        setBrand(data);
      } catch (err) {
        toast.error("Failed to load brand", {
          description: describeApiError(err),
        });
        navigate("/brands");
      } finally {
        setIsLoading(false);
      }
    }
    fetchBrand();
  }, [brandId, navigate]);

  async function handleSubmit(data: Partial<ProductBrand>) {
    setIsSaving(true);
    try {
      await adminApi.brands.update(brandId, data);
      toast.success("Brand updated successfully");
      navigate("/brands");
    } catch (err) {
      toast.error("Failed to update brand", {
        description: describeApiError(err),
      });
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-6 py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!brand) return null;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={`Edit: ${brand.name}`}
        description="Update brand details (renames cascade to product listings)"
        actions={
          <Button variant="outline" onClick={() => navigate("/brands")}>
            <ArrowLeft className="mr-2 size-4" />
            Back to Brands
          </Button>
        }
      />
      <BrandForm initialData={brand} onSubmit={handleSubmit} isLoading={isSaving} />
    </div>
  );
}
